import { format } from 'date-fns';
import type { WhatIfBookingReduction } from '@/types';

/** "6h", "6.5h", "6.25h" */
export function formatReductionHours(hours: number): string {
  return `${Math.round(hours * 100) / 100}h`;
}

/** "−2h" for a cut, "+1h" for an increase, "0h" when unchanged. */
export function formatReductionDelta(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  if (rounded === 0) return '0h';
  return `${rounded < 0 ? '−' : '+'}${Math.abs(rounded)}h`;
}

/** "09:00" for a minute of the day. */
export function formatMinuteOfDay(minute: number): string {
  const m = Math.min(Math.max(Math.round(minute), 0), 24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

function dateFromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
}

/** "Mon 12 Oct" */
export function formatReductionDay(dateKey: string): string {
  return format(dateFromKey(dateKey), 'EEE d MMM');
}

/** "Mon 12 – Wed 14 Oct", or the single day. */
export function formatReductionDateRange(first: string, last: string): string {
  if (first === last) return formatReductionDay(first);
  return `${format(dateFromKey(first), 'EEE d')} – ${formatReductionDay(last)}`;
}

export function formatReductionSpan(reduction: WhatIfBookingReduction): string {
  const first = reduction.days[0]?.dateKey;
  const last = reduction.days[reduction.days.length - 1]?.dateKey;
  return first && last ? formatReductionDateRange(first, last) : '';
}

/** The same before/after hours on every day, e.g. { before: 8, after: 6 }; null when days differ. */
export function uniformDailyHours(
  reduction: WhatIfBookingReduction,
): { before: number; after: number } | null {
  const [first, ...rest] = reduction.days;
  if (!first || rest.length === 0) return null;
  const same = rest.every(
    (d) => Math.abs(d.beforeHours - first.beforeHours) < 1e-6
      && Math.abs(d.afterHours - first.afterHours) < 1e-6,
  );
  return same ? { before: first.beforeHours, after: first.afterHours } : null;
}

/** Tile label for a proposed booking, e.g. "24h → 18h". */
export function reductionTileLabel(reduction: WhatIfBookingReduction): string {
  return `${formatReductionHours(reduction.beforeHours)} → ${formatReductionHours(reduction.afterHours)}`;
}

// The tooltip scrolls, so list every day of up to a month-long booking.
const MAX_TOOLTIP_DAYS = 31;

/**
 * "Proposed change" section appended to a booking's hover tooltip during a
 * reduce-hours preview: total before → after, each day's window and hours,
 * and this booking's share of the whole cut.
 */
export function generateReductionTooltipSection(
  reduction: WhatIfBookingReduction,
  totalCutHours: number,
): string {
  const cut = reduction.beforeHours - reduction.afterHours;
  const daily = uniformDailyHours(reduction);
  const cell = 'padding: 5px 8px 5px 0; white-space: nowrap; vertical-align: top;';
  const rowLine = 'border-bottom: 1px solid rgba(13,148,136,0.18);';

  let html = `<div style="margin: 4px 8px 10px; min-width: 380px; padding: 10px 12px; border: 1px solid rgba(13,148,136,0.45); border-left: 4px solid #0d9488; border-radius: 8px; background: #f0fdfa; color: #111827; font-size: 13px; line-height: 1.4; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;
  html += `<div style="font-size: 12px; font-weight: 700; color: #115e59; text-transform: uppercase; letter-spacing: 0.04em;">Proposed change · not yet saved</div>`;
  html += `<div style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 6px;">`;
  html += `<span style="font-size: 18px; font-weight: 700; color: #4b5563; text-decoration: line-through;">${formatReductionHours(reduction.beforeHours)}</span>`;
  html += `<span style="font-size: 16px; color: #374151;" aria-hidden="true">→</span>`;
  html += `<span style="font-size: 18px; font-weight: 800; color: #115e59;">${formatReductionHours(reduction.afterHours)}</span>`;
  html += `<span style="font-size: 13px; font-weight: 800; color: #991b1b; background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; padding: 1px 8px;">${formatReductionDelta(-cut)}</span>`;
  if (daily) {
    html += `<span style="font-size: 13px; color: #374151;">${formatReductionHours(daily.before)} → ${formatReductionHours(daily.after)} a day</span>`;
  }
  html += `</div>`;

  html += `<table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px;">`;
  html += `<thead><tr style="color: #374151; text-align: left; ${rowLine}">`;
  html += `<th style="${cell} font-weight: 600;">Day</th><th style="${cell} font-weight: 600;">Before</th><th style="${cell} font-weight: 600;">After</th><th style="${cell} padding-right: 0; font-weight: 600; text-align: right;">Change</th>`;
  html += `</tr></thead><tbody>`;
  reduction.days.slice(0, MAX_TOOLTIP_DAYS).forEach((d) => {
    html += `<tr style="${rowLine}">`;
    html += `<td style="${cell} font-weight: 600; color: #111827;">${formatReductionDay(d.dateKey)}</td>`;
    html += `<td style="${cell} color: #374151;">${formatMinuteOfDay(d.beforeStartMin)}–${formatMinuteOfDay(d.beforeEndMin)} · ${formatReductionHours(d.beforeHours)}</td>`;
    html += `<td style="${cell} color: #111827; font-weight: 700;">${formatMinuteOfDay(d.afterStartMin)}–${formatMinuteOfDay(d.afterEndMin)} · ${formatReductionHours(d.afterHours)}</td>`;
    html += `<td style="${cell} padding-right: 0; color: #991b1b; font-weight: 700; text-align: right;">${formatReductionDelta(d.afterHours - d.beforeHours)}</td>`;
    html += `</tr>`;
  });
  if (reduction.days.length > MAX_TOOLTIP_DAYS) {
    html += `<tr style="${rowLine}"><td colspan="4" style="${cell} color: #374151;">+${reduction.days.length - MAX_TOOLTIP_DAYS} more days — see View breakdown in the banner</td></tr>`;
  }
  html += `</tbody><tfoot><tr>`;
  html += `<td style="${cell} padding-top: 6px; font-weight: 800; color: #111827;">Total</td>`;
  html += `<td style="${cell} padding-top: 6px; font-weight: 700; color: #374151;">${formatReductionHours(reduction.beforeHours)}</td>`;
  html += `<td style="${cell} padding-top: 6px; font-weight: 800; color: #111827;">${formatReductionHours(reduction.afterHours)}</td>`;
  html += `<td style="${cell} padding-top: 6px; padding-right: 0; font-weight: 800; color: #991b1b; text-align: right;">${formatReductionDelta(-cut)}</td>`;
  html += `</tr></tfoot></table>`;

  if (totalCutHours > 0 && cut > 0) {
    const share = Math.round((cut / totalCutHours) * 100);
    html += `<div style="font-size: 13px; color: #111827; margin-top: 8px;">This booking takes <b>${formatReductionHours(cut)} of the ${formatReductionHours(totalCutHours)}</b> total cut (${share}%).</div>`;
  }
  html += `</div>`;
  return html;
}
